/**
 * Filters an array to contain only unique elements based on a key selector function.
 * Uses Set for efficient uniqueness checks.
 *
 * @param array The input array to filter
 * @param keySelector A function that extracts the key to determine uniqueness
 * @returns A new array containing only the first occurrence of each unique element
 *
 * @example
 * // Filter unique numbers
 * uniqueBy([1, 2, 2, 3, 4, 1], num => num);
 * // Result: [1, 2, 3, 4]
 *
 * @example
 * // Filter users with unique IDs
 * uniqueBy(users, user => user.id);
 */
export const filterUniqueElements = <T, K>(
  array: T[],
  keySelector: (item: T) => K,
): T[] => {
  if (array.length <= 1) {
    return [...array];
  }

  const seen = new Set<K>();
  const result: T[] = [];

  for (const item of array) {
    const key = keySelector(item);
    if (!seen.has(key)) {
      seen.add(key);
      result.push(item);
    }
  }

  return result;
};
